import React, { useEffect } from 'react';
import {
  Box,
  Typography,
  TextField,
  Button,
  Alert,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tooltip,
  IconButton,
  List,
  ListItem,
  ListItemText,
  CircularProgress,
} from '@mui/material';
import {
  INTEGRATIONS_API_BASE,
  JN_CONFIG_API_BASE,
} from 'application/config/apiConfig';
import { useListStageCategories } from 'application/hooks/useListStageCategories';
import { useListStages } from 'application/hooks/useListStages';
import {
  pickBestConfig,
  patchIntegrationConfig,
  deleteIntegrationConfig,
  createIntegrationConfig,
  updateIntegrationConfig,
  listIntegrationConfigs,
} from '../utils/integrationConfigUtils';
import {
  findContactsCategory,
  extractFlexFields,
} from '../utils/flexFieldUtils';
import {
  normalizeCustomFields,
  convertFieldMappingLabels,
  sortMappingsByPaivSequence,
  sortMappingsByJobNimbusOrder,
} from '../utils/jobNimbusUtils';
import { ConfirmationModal } from './ConfirmationModal';
import { MappingRow } from './shared/MappingRow';
import { WebhookUrlDisplay } from './shared/WebhookUrlDisplay';
import { whiteSelectSx, whiteSelectMenuProps } from './shared/selectStyles';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import ClearIcon from '@mui/icons-material/Clear';
import { Add as AddIcon } from '@mui/icons-material';

export const JobNimbusSetup: React.FC = () => {
  const [apiKey, setApiKey] = React.useState('');
  const [selectedWorkflow, setSelectedWorkflow] = React.useState<string>('');
  const [availableWorkflows, setAvailableWorkflows] = React.useState<any[]>([]);
  const [isFetching, setIsFetching] = React.useState(false);
  const [jobnimbusStages, setJobnimbusStages] = React.useState<
    Array<{
      id: string;
      name: string;
      description?: string;
      order?: number;
      numericId?: number;
    }>
  >([]);
  const [paivStages, setPaivStages] = React.useState<
    Array<{
      id: string;
      title: string;
      description?: string;
      sequence?: number;
    }>
  >([]);
  const [mappings, setMappings] = React.useState<
    Array<{
      jobnimbusStage: string;
      jobnimbusStageId: string;
      paivStage: string;
      paivStageId: string;
    }>
  >([]);
  const [error, setError] = React.useState<string | null>(null);
  const [saveMsg, setSaveMsg] = React.useState<string | null>(null);
  const [isSaving, setIsSaving] = React.useState(false);
  const [isEditing, setIsEditing] = React.useState(true);
  const [contactsCategoryID, setContactsCategoryID] = React.useState<
    string | null
  >(null);
  const [webhookUrl, setWebhookUrl] = React.useState<string | null>(null);
  const [existingIntegrationId, setExistingIntegrationId] = React.useState<
    string | null
  >(null);
  const [isActive, setIsActive] = React.useState<boolean | null>(null);
  const [isDisableOpen, setIsDisableOpen] = React.useState(false);
  const [isToggling, setIsToggling] = React.useState(false);
  const [loadedApiKey, setLoadedApiKey] = React.useState<string>('');
  const [loadedSelectedWorkflow, setLoadedSelectedWorkflow] =
    React.useState<string>('');
  const [loadedSelectedWorkflowName, setLoadedSelectedWorkflowName] =
    React.useState<string>('');
  const [loadedAvailableWorkflows, setLoadedAvailableWorkflows] =
    React.useState<any[]>([]);
  const [loadedJobnimbusStages, setLoadedJobnimbusStages] = React.useState<
    Array<{
      id: string;
      name: string;
      description?: string;
      order?: number;
      numericId?: number;
    }>
  >([]);
  const [loadedMappings, setLoadedMappings] = React.useState<
    Array<{
      jobnimbusStage: string;
      jobnimbusStageId: string;
      paivStage: string;
      paivStageId: string;
    }>
  >([]);
  const [webhookDialogOpen, setWebhookDialogOpen] = React.useState(false);
  const [isRemoveOpen, setIsRemoveOpen] = React.useState(false);

  // Config options state
  const [defaultRecordType, setDefaultRecordType] =
    React.useState<string>('job'); // 'job' or 'contact'
  const [loadedDefaultRecordType, setLoadedDefaultRecordType] =
    React.useState<string>('job');
  const [jobNameFormat, setJobNameFormat] = React.useState<string>('name_only'); // 'name_only', 'name_and_date', 'name_and_address', 'name_address_date'
  const [loadedJobNameFormat, setLoadedJobNameFormat] =
    React.useState<string>('name_only');
  const [defaultStatus, setDefaultStatus] = React.useState<string>('');
  const [loadedDefaultStatus, setLoadedDefaultStatus] =
    React.useState<string>('');

  // Field mappings state
  const [availableFlexFields, setAvailableFlexFields] = React.useState<
    Array<{ name: string; label: string }>
  >([]);
  const [jobnimbusCustomFields, setJobnimbusCustomFields] = React.useState<
    Array<{ name: string; label: string }>
  >([]);
  const [fieldMappings, setFieldMappings] = React.useState<
    Record<string, string>
  >({}); // Paiv field name -> JobNimbus field name
  const [loadedFieldMappings, setLoadedFieldMappings] = React.useState<
    Record<string, string>
  >({});
  const [enabledFieldMappings, setEnabledFieldMappings] = React.useState<
    Array<string>
  >([]); // Array of Paiv field names that are enabled for mapping
  const [loadedEnabledFieldMappings, setLoadedEnabledFieldMappings] =
    React.useState<Array<string>>([]);
  const [addFieldDialogOpen, setAddFieldDialogOpen] = React.useState(false);
  const [isInitialLoading, setIsInitialLoading] = React.useState(true);
  const [helpDialogOpen, setHelpDialogOpen] = React.useState(false);
  // Cache for workflow API responses keyed by apiKey+objectType
  const workflowCacheRef = React.useRef<
    Map<string, { workflows: any[]; customFields: any[] }>
  >(new Map());

  // Load Contacts category flex fields and Paiv stages via cached hooks
  const { data: categories = [] } = useListStageCategories();
  const { data: stages = [] } = useListStages();

  useEffect(() => {
    const contacts = findContactsCategory(categories);
    setContactsCategoryID(contacts?.id ?? null);
    setAvailableFlexFields(extractFlexFields(contacts?.flexAttributes));
  }, [categories]);

  useEffect(() => {
    setPaivStages(
      stages.map((s) => ({
        id: s.id,
        title: s.title,
        description: s.description,
        sequence: s.sequence,
      }))
    );
  }, [stages]);

  // Load existing JobNimbus integration from new REST endpoint
  useEffect(() => {
    (async () => {
      const orgID = (global as any)?.me?.orgID;
      if (!orgID) {
        setIsInitialLoading(false);
        return;
      }

      try {
        const res = await listIntegrationConfigs(JN_CONFIG_API_BASE, orgID);
        const items = res.success ? res.items ?? [] : [];
        const picked = pickBestConfig(items);

        if (!picked) {
          setExistingIntegrationId(null);
          setIsActive(null);
          setWebhookUrl(null);
          setApiKey('');
          setLoadedApiKey('');
          setSelectedWorkflow('');
          setLoadedSelectedWorkflow('');
          setAvailableWorkflows([]);
          setJobnimbusStages([]);
          setLoadedJobnimbusStages([]);
          setMappings([]);
          setLoadedMappings([]);
          setDefaultRecordType('job');
          setLoadedDefaultRecordType('job');
          setJobNameFormat('name_only');
          setLoadedJobNameFormat('name_only');
          setDefaultStatus('');
          setLoadedDefaultStatus('');
          setIsEditing(true);
          setIsInitialLoading(false);
          return;
        }

        setExistingIntegrationId(picked.id ?? null);
        setIsActive(Boolean(picked.isActive));

        // New schema: everything is inside configOptions (already an object)
        const co = picked.configOptions ?? {};

        const pickedApiKey = co.apiKey ?? '';
        setApiKey(pickedApiKey);
        setLoadedApiKey(pickedApiKey);

        const workflowName = co.defaultRecordType || '';
        setLoadedSelectedWorkflowName(workflowName);
        setSelectedWorkflow('');
        setLoadedSelectedWorkflow('');

        const recordType = co.mainObjectType || 'job';
        setDefaultRecordType(recordType);
        setLoadedDefaultRecordType(recordType);
        setJobNameFormat(co.jobNameFormat || 'name_only');
        setLoadedJobNameFormat(co.jobNameFormat || 'name_only');
        setDefaultStatus(co.defaultStatus || '');
        setLoadedDefaultStatus(co.defaultStatus || '');

        // fieldMapping is inside configOptions
        const fieldMapping =
          co.fieldMapping && typeof co.fieldMapping === 'object'
            ? (co.fieldMapping as Record<string, string>)
            : {};
        setFieldMappings(fieldMapping);
        setLoadedFieldMappings(fieldMapping);
        const enabledFields = Object.keys(fieldMapping);
        setEnabledFieldMappings(enabledFields);
        setLoadedEnabledFieldMappings(enabledFields);

        // Fetch workflows if we have an API key
        if (pickedApiKey) {
          try {
            const workflowResponse = await fetch(
              `${INTEGRATIONS_API_BASE}/jobNimbus/stages`,
              {
                method: 'POST',
                headers: {
                  'Content-Type': 'application/json',
                  Authorization: `Bearer ${(window as any)?.me?.accessToken}`,
                },
                body: JSON.stringify({ apiKey: pickedApiKey.trim() }),
              }
            );
            if (workflowResponse.ok) {
              const workflowData = await workflowResponse.json();
              if (
                workflowData.success &&
                Array.isArray(workflowData.workflows)
              ) {
                setAvailableWorkflows(workflowData.workflows);
                setLoadedAvailableWorkflows(workflowData.workflows);

                if (workflowName) {
                  const matched = workflowData.workflows.find(
                    (w: any) => w.name === workflowName
                  );
                  if (matched) {
                    setSelectedWorkflow(matched.id);
                    setLoadedSelectedWorkflow(matched.id);
                    setLoadedSelectedWorkflowName(workflowName);
                  }
                }
              }
              if (workflowData.custom_fields_job) {
                const fields = normalizeCustomFields(
                  workflowData.custom_fields_job
                );
                setJobnimbusCustomFields(fields);

                if (fields.length > 0 && Object.keys(fieldMapping).length > 0) {
                  const converted = convertFieldMappingLabels(
                    fieldMapping,
                    fields
                  );
                  if (converted !== fieldMapping) {
                    setFieldMappings(converted);
                    setLoadedFieldMappings(converted);
                  }
                }
              }
            }
          } catch (err) {
            console.error(
              '[JobNimbusSetup] Error fetching workflows on load',
              err
            );
          }
        }

        // stageMapping is inside configOptions
        const mappingObj =
          co.stageMapping && typeof co.stageMapping === 'object'
            ? co.stageMapping
            : {};
        const entries = Object.keys(mappingObj).map((name, idx) => ({
          name,
          paivStageId: String(mappingObj[name] || ''),
          position: idx + 1,
        }));

        const jobnimbusList = entries.map((e) => ({
          id: e.name,
          name: e.name,
        })) as any;
        setJobnimbusStages(jobnimbusList);
        setLoadedJobnimbusStages(jobnimbusList);

        const mapList = entries.map((e) => ({
          jobnimbusStage: e.name,
          jobnimbusStageId: e.name,
          paivStage:
            paivStages.find((s) => s.id === e.paivStageId)?.title || '',
          paivStageId: e.paivStageId,
        })) as any;

        const sortedMapList = sortMappingsByPaivSequence(mapList, paivStages);
        setMappings(sortedMapList);
        setLoadedMappings(sortedMapList);

        const derivedWebhook = `https://api.gogamify.com/webhooks/jobnimbus/${picked.id}`;
        setWebhookUrl(derivedWebhook);
        setIsEditing(false);
      } catch (err) {
        console.error('[JobNimbusSetup] Error loading integration:', err);
      } finally {
        setIsInitialLoading(false);
      }
    })();
  }, []);

  // Fetch JobNimbus custom fields when API key exists and convert field mappings
  useEffect(() => {
    (async () => {
      if (!apiKey.trim()) return;

      try {
        const response = await fetch(
          `${INTEGRATIONS_API_BASE}/jobNimbus/stages`,
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${(window as any)?.me?.accessToken}`,
            },
            body: JSON.stringify({ apiKey: apiKey.trim() }),
          }
        );

        if (response.ok) {
          const data = await response.json();
          const fields = normalizeCustomFields(data.custom_fields_job);
          setJobnimbusCustomFields(fields);

          setFieldMappings((current) => {
            if (
              !current ||
              typeof current !== 'object' ||
              Array.isArray(current)
            )
              return {};
            const converted = convertFieldMappingLabels(current, fields);
            if (converted !== current) setLoadedFieldMappings(converted);
            return converted;
          });
        }
      } catch (err) {
        console.error(
          '[JobNimbusSetup] Error fetching JobNimbus custom fields',
          err
        );
      }
    })();
  }, [apiKey]);

  // Auto-fetch workflows when API key or main object type changes
  useEffect(() => {
    if (!isEditing) return; // Don't auto-fetch when not in edit mode
    if (!apiKey.trim() || !defaultRecordType) return; // Need both to fetch

    // Only fetch if we don't already have workflows for this object type
    // or if the workflows list is empty
    const hasWorkflowsForType = availableWorkflows.some(
      (w: any) =>
        String(w.object_type || '').toLowerCase() ===
        String(defaultRecordType || '').toLowerCase()
    );

    if (availableWorkflows.length === 0 || !hasWorkflowsForType) {
      fetchJobnimbusWorkflows();
    }
  }, [apiKey, defaultRecordType, isEditing]);

  // Auto-fetch stages when workflow is selected
  useEffect(() => {
    if (!isEditing) return; // Don't auto-fetch when not in edit mode
    if (!selectedWorkflow || selectedWorkflow === 'saved-workflow') return; // Need a valid workflow
    if (!apiKey.trim()) return; // Need API key

    // Only fetch if we don't already have stages for this workflow
    if (jobnimbusStages.length === 0 || mappings.length === 0) {
      fetchJobnimbusStages();
    }
  }, [selectedWorkflow, isEditing, apiKey]);

  const fetchJobnimbusWorkflows = async () => {
    if (!apiKey.trim()) {
      setError('Please enter your JobNimbus API key');
      return;
    }

    if (!defaultRecordType) {
      setError('Please select a main object type');
      return;
    }

    // Check cache first
    const cacheKey = `${apiKey.trim()}|${defaultRecordType}`;
    const cached = workflowCacheRef.current.get(cacheKey);
    if (cached) {
      const filteredWorkflows = cached.workflows.filter(
        (w: any) =>
          String(w.object_type || '').toLowerCase() ===
          String(defaultRecordType || '').toLowerCase()
      );
      setAvailableWorkflows(filteredWorkflows);
      if (cached.customFields.length > 0) {
        setJobnimbusCustomFields(cached.customFields);
      }
      if (loadedSelectedWorkflow && filteredWorkflows.length > 0) {
        const matched = filteredWorkflows.find(
          (w: any) => w.id === loadedSelectedWorkflow
        );
        if (matched) setSelectedWorkflow(loadedSelectedWorkflow);
        else setSelectedWorkflow('');
      }
      return;
    }

    setIsFetching(true);
    setError(null);

    try {
      const response = await fetch(
        `${INTEGRATIONS_API_BASE}/jobNimbus/stages`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${(window as any)?.me?.accessToken}`,
          },
          body: JSON.stringify({
            apiKey: apiKey.trim(),
            object_type: defaultRecordType,
          }),
        }
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || `HTTP ${response.status}`);
      }

      const data = await response.json();

      if (data.success && Array.isArray(data.workflows)) {
        const filteredWorkflows = data.workflows.filter(
          (w: any) =>
            String(w.object_type || '').toLowerCase() ===
            String(defaultRecordType || '').toLowerCase()
        );
        setAvailableWorkflows(filteredWorkflows);

        if (loadedSelectedWorkflow && filteredWorkflows.length > 0) {
          const matched = filteredWorkflows.find(
            (w: any) => w.id === loadedSelectedWorkflow
          );
          if (matched) setSelectedWorkflow(loadedSelectedWorkflow);
          else setSelectedWorkflow('');
        }

        // Cache the result
        const customFields = data.custom_fields_job
          ? normalizeCustomFields(data.custom_fields_job)
          : [];
        workflowCacheRef.current.set(cacheKey, {
          workflows: data.workflows,
          customFields,
        });

        setError(null);
      } else {
        throw new Error(data.message || 'Failed to fetch JobNimbus workflows');
      }

      if (data.custom_fields_job) {
        setJobnimbusCustomFields(normalizeCustomFields(data.custom_fields_job));
      }
    } catch (err: any) {
      console.error('[JobNimbusSetup] fetch workflows error', err);
      setError(err.message || 'Failed to fetch JobNimbus workflows');
    } finally {
      setIsFetching(false);
    }
  };

  const fetchJobnimbusStages = async () => {
    if (!selectedWorkflow) {
      setError('Please select a workflow first');
      return;
    }

    setIsFetching(true);
    setError(null);

    try {
      const response = await fetch(
        `${INTEGRATIONS_API_BASE}/jobNimbus/stages`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${(window as any)?.me?.accessToken}`,
          },
          body: JSON.stringify({
            apiKey: apiKey.trim(),
            workflowId: selectedWorkflow,
          }),
        }
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || `HTTP ${response.status}`);
      }

      const data = await response.json();

      if (data.success && Array.isArray(data.stages)) {
        const stages = data.stages.map((stage: any) => ({
          id: stage.id || stage.stageId || String(Math.random()),
          name: stage.name || stage.title || 'Unnamed Stage',
          description: stage.description || '',
          order: typeof stage.order === 'number' ? stage.order : undefined,
          numericId:
            typeof stage.id === 'number'
              ? stage.id
              : typeof stage.id === 'string'
              ? parseInt(stage.id, 10)
              : undefined,
        }));

        // Sort stages by order field first, then by id if order is not available
        const sortedStages = [...stages].sort((a, b) => {
          // If both have order, sort by order
          if (a.order !== undefined && b.order !== undefined) {
            return a.order - b.order;
          }
          // If only one has order, prioritize it
          if (a.order !== undefined) return -1;
          if (b.order !== undefined) return 1;
          // If no order, sort by numeric id
          if (a.numericId !== undefined && b.numericId !== undefined) {
            return a.numericId - b.numericId;
          }
          // If only one has numeric id, prioritize it
          if (a.numericId !== undefined) return -1;
          if (b.numericId !== undefined) return 1;
          // If neither has order or numeric id, maintain original order
          return 0;
        });

        setJobnimbusStages(sortedStages);

        // Initialize mappings with empty Paiv stage selections (using sorted stages)
        const initialMappings = sortedStages.map((stage: any) => ({
          jobnimbusStage: stage.name,
          jobnimbusStageId: stage.id,
          paivStage: '',
          paivStageId: '',
        }));
        // Sort mappings by JobNimbus order when actively mapping
        const sortedMappings = sortMappingsByJobNimbusOrder(
          initialMappings,
          jobnimbusStages
        );
        setMappings(sortedMappings);
        setError(null);
      } else {
        throw new Error(data.message || 'Failed to fetch JobNimbus stages');
      }
    } catch (err: any) {
      console.error('[JobNimbusSetup] fetch stages error', err);
      setError(err.message || 'Failed to fetch JobNimbus stages');
    } finally {
      setIsFetching(false);
    }
  };

  const handleSave = async () => {
    if (!apiKey.trim()) {
      setError('Please enter your JobNimbus API key');
      return;
    }

    if (mappings.length === 0) {
      setError('Please fetch JobNimbus stages first');
      return;
    }

    const actualWorkflowId =
      selectedWorkflow === 'saved-workflow'
        ? loadedSelectedWorkflow
        : selectedWorkflow;
    if (!actualWorkflowId) {
      setError('Please select a workflow first');
      return;
    }

    setIsSaving(true);
    setError(null);

    const webhookBaseUrl = 'https://api.gogamify.com/webhooks/jobnimbus';

    try {
      const orgID = (window as any)?.me?.orgID;
      if (!orgID) throw new Error('Organization ID not found');

      // Build stageMapping as { "JN Status Name": "paiv-stage-uuid" }
      const stageMapping: Record<string, string> = {};
      mappings.forEach((m) => {
        if (m.jobnimbusStage && m.paivStageId) {
          stageMapping[m.jobnimbusStage] = m.paivStageId;
        }
      });

      const configOptions: Record<string, any> = {
        apiKey: apiKey.trim(),
        mainObjectType: defaultRecordType || 'job',
        stageMapping,
        fieldMapping: fieldMappings || {},
        userMapping: {}, // auto-populated by backend
        defaultRecordType:
          availableWorkflows.find((w) => w.id === actualWorkflowId)?.name ||
          loadedSelectedWorkflowName ||
          actualWorkflowId,
        jobNameFormat: jobNameFormat || 'name_only',
        defaultStatus: defaultStatus || '',
      };

      const payload = {
        orgID,
        categoryID: contactsCategoryID,
        configOptions,
      };

      let savedId: string;

      if (existingIntegrationId) {
        // PUT full update
        const res = await updateIntegrationConfig(
          JN_CONFIG_API_BASE,
          existingIntegrationId,
          payload
        );
        if (!res.success) throw new Error(res.error || 'Failed to update');
        savedId = existingIntegrationId;
      } else {
        // POST create
        const res = await createIntegrationConfig(JN_CONFIG_API_BASE, payload);
        if (!res.success) throw new Error(res.error || 'Failed to create');
        savedId = res.id || '';
      }

      setExistingIntegrationId(savedId);
      setWebhookUrl(`${webhookBaseUrl}/${savedId}`);
      setSaveMsg('JobNimbus integration saved successfully!');
      setIsEditing(false);
      setIsActive(true);
      setWebhookDialogOpen(true);

      // Update loaded state for cancel functionality
      setLoadedApiKey(apiKey);
      setLoadedJobnimbusStages([...jobnimbusStages]);
      setLoadedMappings([...mappings]);
      setLoadedDefaultRecordType(defaultRecordType);
      setLoadedJobNameFormat(jobNameFormat);
      setLoadedDefaultStatus(defaultStatus);
      setLoadedFieldMappings({ ...fieldMappings });
      setLoadedEnabledFieldMappings([...enabledFieldMappings]);
    } catch (err: any) {
      console.error('[JobNimbusSetup] save error', err);
      setError(err.message || 'Failed to save JobNimbus integration');
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancel = () => {
    setApiKey(loadedApiKey);
    setSelectedWorkflow(loadedSelectedWorkflow);
    setAvailableWorkflows([...loadedAvailableWorkflows]); // Restore workflows
    setJobnimbusStages([...loadedJobnimbusStages]);
    setMappings([...loadedMappings]);
    setDefaultRecordType(loadedDefaultRecordType);
    setJobNameFormat(loadedJobNameFormat);
    setDefaultStatus(loadedDefaultStatus);
    setFieldMappings({ ...loadedFieldMappings });
    setEnabledFieldMappings([...loadedEnabledFieldMappings]);
    setAddFieldDialogOpen(false);
    setIsEditing(false);
    setError(null);
    setSaveMsg(null);
  };

  const handleEnable = async () => {
    if (!existingIntegrationId) return;
    setIsToggling(true);
    setError(null);
    setSaveMsg(null);
    try {
      const res = await patchIntegrationConfig(
        JN_CONFIG_API_BASE,
        existingIntegrationId,
        { isActive: true }
      );
      if (res.success) {
        setIsActive(true);
        setSaveMsg('JobNimbus integration enabled successfully');
      } else {
        throw new Error(res.error || 'Failed to enable integration');
      }
    } catch (err: any) {
      setError(err?.message || 'Failed to enable integration');
    } finally {
      setIsToggling(false);
    }
  };

  const handleDisable = async () => {
    if (!existingIntegrationId) return;
    setIsToggling(true);
    setError(null);
    setSaveMsg(null);
    try {
      const res = await patchIntegrationConfig(
        JN_CONFIG_API_BASE,
        existingIntegrationId,
        { isActive: false }
      );
      if (res.success) {
        setIsActive(false);
        setSaveMsg('JobNimbus integration disabled successfully');
      } else {
        throw new Error(res.error || 'Failed to disable integration');
      }
    } catch (err: any) {
      setError(err?.message || 'Failed to disable integration');
    } finally {
      setIsToggling(false);
      setIsDisableOpen(false);
    }
  };

  const handleRemove = async () => {
    if (!existingIntegrationId) return;
    setIsToggling(true);
    try {
      const res = await deleteIntegrationConfig(
        JN_CONFIG_API_BASE,
        existingIntegrationId
      );
      if (res.success) {
        setExistingIntegrationId(null);
        setIsActive(null);
        setWebhookUrl(null);
        setApiKey('');
        setLoadedApiKey('');
        setSelectedWorkflow('');
        setLoadedSelectedWorkflow('');
        setAvailableWorkflows([]);
        setJobnimbusStages([]);
        setLoadedJobnimbusStages([]);
        setMappings([]);
        setLoadedMappings([]);
        setDefaultRecordType('job');
        setLoadedDefaultRecordType('job');
        setJobNameFormat('name_only');
        setLoadedJobNameFormat('name_only');
        setDefaultStatus('');
        setLoadedDefaultStatus('');
        setFieldMappings({});
        setLoadedFieldMappings({});
        setEnabledFieldMappings([]);
        setLoadedEnabledFieldMappings([]);
        setIsEditing(true);
        setSaveMsg('JobNimbus integration removed successfully');
      } else {
        throw new Error(res.error || 'Failed to remove integration');
      }
    } catch (err) {
      setError('Failed to remove integration');
    } finally {
      setIsToggling(false);
      setIsRemoveOpen(false);
    }
  };

  const isConnected = isActive === true;
  const isDisabled = isActive === false;

  const canSave = Boolean(apiKey.trim());

  // Pre-sort JN stages for dropdown rendering
  const sortedJnStages = React.useMemo(
    () =>
      [...jobnimbusStages].sort((a, b) => {
        if (a.order !== undefined && b.order !== undefined)
          return a.order - b.order;
        if (a.order !== undefined) return -1;
        if (b.order !== undefined) return 1;
        if (a.numericId !== undefined && b.numericId !== undefined)
          return a.numericId - b.numericId;
        if (a.numericId !== undefined) return -1;
        if (b.numericId !== undefined) return 1;
        return 0;
      }),
    [jobnimbusStages]
  );

  // Compute if Add Field Mapping button should be disabled
  const isAddFieldButtonDisabled = React.useMemo(() => {
    return enabledFieldMappings.length >= availableFlexFields.length;
  }, [enabledFieldMappings.length, availableFlexFields.length]);

  // Show loading spinner while initial data is being loaded
  if (isInitialLoading) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '400px',
          mt: 10,
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ maxWidth: 1200, mx: 'auto', p: 3, mt: 10 }}>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          gap: 4,
          mb: 3,
        }}
      >
        <Typography variant="h4" component="h1">
          JobNimbus Integration Setup
        </Typography>
        <Box sx={{ display: 'flex', gap: 2 }}>
          {isDisabled && !isEditing && (
            <Button
              variant="contained"
              color="success"
              onClick={handleEnable}
              disabled={isToggling}
            >
              {isToggling ? 'Enabling...' : 'Enable Integration'}
            </Button>
          )}
          {isConnected && !isEditing && (
            <Button
              variant="outlined"
              color="warning"
              onClick={() => setIsDisableOpen(true)}
              disabled={isToggling}
            >
              Disable Integration
            </Button>
          )}
          {isConnected && isEditing && (
            <Button
              variant="outlined"
              color="error"
              onClick={() => setIsRemoveOpen(true)}
              disabled={isToggling}
            >
              Remove Integration
            </Button>
          )}
          {!isEditing && (
            <Button
              variant="outlined"
              onClick={() => {
                setIsEditing(true);
                setAvailableWorkflows([]);
              }}
            >
              Edit Configuration
            </Button>
          )}
        </Box>
      </Box>

      {webhookUrl && (
        <Alert severity="info" sx={{ mb: 3 }}>
          <WebhookUrlDisplay
            url={webhookUrl}
            onHelpClick={() => setHelpDialogOpen(true)}
          />
        </Alert>
      )}

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {saveMsg && (
        <Alert severity="success" sx={{ mb: 3 }}>
          {saveMsg}
        </Alert>
      )}

      <Box sx={{ mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
          <Typography variant="h6">API Key</Typography>
          <Tooltip title="Get your API key from JobNimbus Settings > API Keys">
            <IconButton size="small" onClick={() => setHelpDialogOpen(true)}>
              <HelpOutlineIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
        <TextField
          fullWidth
          type="password"
          value={apiKey}
          onChange={(e) => setApiKey(e.target.value)}
          placeholder="Enter your JobNimbus API key"
          disabled={!isEditing}
          sx={{ mb: 2 }}
        />

        <FormControl fullWidth sx={{ mb: 2 }}>
          <InputLabel>Main Object Type</InputLabel>
          <Select
            value={defaultRecordType}
            onChange={(e) => {
              const newType = e.target.value;
              setDefaultRecordType(newType);
              // Clear workflows when object type changes (will auto-fetch via useEffect)
              setAvailableWorkflows([]);
              setSelectedWorkflow('');
              setJobnimbusStages([]);
              setMappings([]);
            }}
            disabled={!isEditing}
            label="Main Object Type"
            sx={whiteSelectSx}
            MenuProps={whiteSelectMenuProps}
          >
            <MenuItem value="job">Job</MenuItem>
            <MenuItem value="contact">Contact</MenuItem>
          </Select>
        </FormControl>

        {isFetching && availableWorkflows.length === 0 && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
            <CircularProgress size={20} />
            <Typography variant="body2" color="text.secondary">
              Fetching workflows...
            </Typography>
          </Box>
        )}

        {(availableWorkflows.length > 0 || selectedWorkflow) && (
          <Box sx={{ mb: 2 }}>
            <Typography variant="h6" sx={{ mb: 1 }}>
              Select Workflow
            </Typography>
            <FormControl fullWidth>
              <Select
                value={
                  availableWorkflows.length > 0
                    ? selectedWorkflow
                    : loadedSelectedWorkflowName
                    ? 'saved-workflow'
                    : ''
                }
                onChange={(e) => {
                  const newWorkflow = e.target.value;
                  if (newWorkflow === 'saved-workflow') return; // Prevent changing when workflows aren't loaded
                  setSelectedWorkflow(newWorkflow);
                  // Clear stages and mappings when workflow changes (will auto-fetch via useEffect)
                  if (newWorkflow !== selectedWorkflow && newWorkflow !== '') {
                    setJobnimbusStages([]);
                    setMappings([]);
                  }
                }}
                disabled={!isEditing || availableWorkflows.length === 0}
                displayEmpty
                renderValue={(value) => {
                  if (!value || value === '')
                    return <em>Choose a workflow...</em>;
                  if (value === 'saved-workflow') {
                    // Show saved workflow name when workflows aren't loaded
                    return loadedSelectedWorkflowName || 'Saved Workflow';
                  }
                  // If workflows are loaded, show the workflow name
                  if (availableWorkflows.length > 0) {
                    const workflow = availableWorkflows.find(
                      (w) => w.id === value
                    );
                    return workflow ? workflow.name : value;
                  }
                  // Fallback: show the value
                  return value;
                }}
                sx={whiteSelectSx}
                MenuProps={whiteSelectMenuProps}
              >
                <MenuItem value="">
                  <em>Choose a workflow...</em>
                </MenuItem>
                {availableWorkflows.length === 0 &&
                  loadedSelectedWorkflowName && (
                    <MenuItem value="saved-workflow" disabled>
                      {loadedSelectedWorkflowName}
                    </MenuItem>
                  )}
                {availableWorkflows.map((workflow) => (
                  <MenuItem key={workflow.id} value={workflow.id}>
                    {workflow.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {isFetching &&
              jobnimbusStages.length === 0 &&
              selectedWorkflow &&
              selectedWorkflow !== 'saved-workflow' && (
                <Box
                  sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 2 }}
                >
                  <CircularProgress size={20} />
                  <Typography variant="body2" color="text.secondary">
                    Fetching stages...
                  </Typography>
                </Box>
              )}
          </Box>
        )}
      </Box>

      {/* Config Options Section */}
      {selectedWorkflow && (
        <Box sx={{ mb: 3 }}>
          <Typography variant="h6" sx={{ mb: 2 }}>
            Config Options
          </Typography>
          <Box
            sx={{
              p: 3,
              backgroundColor: 'grey.50',
              borderRadius: 1,
              display: 'flex',
              flexDirection: 'column',
              gap: 3,
            }}
          >
            <Box>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                Selected Workflow
              </Typography>
              <Typography variant="body1" fontWeight="medium">
                {availableWorkflows.length > 0
                  ? availableWorkflows.find((w) => w.id === selectedWorkflow)
                      ?.name || selectedWorkflow
                  : loadedSelectedWorkflowName || selectedWorkflow}
              </Typography>
            </Box>

            <Box>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                Main Object Type
              </Typography>
              <Typography variant="body1" fontWeight="medium">
                {defaultRecordType === 'job' ? 'Job' : 'Contact'}
              </Typography>
            </Box>

            <FormControl fullWidth size="small">
              <InputLabel>Job Name Format</InputLabel>
              <Select
                value={jobNameFormat}
                onChange={(e) => setJobNameFormat(e.target.value)}
                disabled={!isEditing}
                label="Job Name Format"
                sx={whiteSelectSx}
                MenuProps={whiteSelectMenuProps}
              >
                <MenuItem value="name_only">Name Only</MenuItem>
                <MenuItem value="name_with_date">Name with Date</MenuItem>
                <MenuItem value="name_with_job_number">
                  Name with Job Number
                </MenuItem>
              </Select>
            </FormControl>
          </Box>
        </Box>
      )}

      {jobnimbusStages.length > 0 && paivStages.length > 0 && (
        <Box sx={{ mb: 3 }}>
          <Typography variant="h6" sx={{ mb: 2 }}>
            Stage Mappings
          </Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {[...paivStages]
              .sort((a, b) => {
                const seqA = a.sequence ?? Number.MAX_SAFE_INTEGER;
                const seqB = b.sequence ?? Number.MAX_SAFE_INTEGER;
                if (seqA !== seqB) return seqA - seqB;
                return a.title.localeCompare(b.title);
              })
              .map((paivStage) => {
                // Find the mapping for this Paiv stage
                const mapping = mappings.find(
                  (m) => m.paivStageId === paivStage.id
                );
                const mappedJobnimbusStageId =
                  mapping?.jobnimbusStageId || mapping?.jobnimbusStage || '';

                return (
                  <MappingRow
                    key={paivStage.id}
                    leftLabel="Paiv Stage"
                    leftValue={paivStage.title}
                    rightLabel="JobNimbus Stage"
                    rightContent={
                      <FormControl fullWidth size="small">
                        <Select
                          value={mappedJobnimbusStageId || ''}
                          onChange={(e) => {
                            const selectedJobnimbusStageId = e.target.value;
                            const selectedJobnimbusStage = jobnimbusStages.find(
                              (s) =>
                                s.id === selectedJobnimbusStageId ||
                                s.name === selectedJobnimbusStageId
                            );

                            // Remove any existing mapping for this Paiv stage
                            let newMappings = mappings.filter(
                              (m) => m.paivStageId !== paivStage.id
                            );

                            // Remove any existing mapping for the selected JobNimbus stage (one-to-one mapping)
                            if (selectedJobnimbusStageId) {
                              newMappings = newMappings.filter(
                                (m) =>
                                  m.jobnimbusStageId !==
                                    selectedJobnimbusStageId &&
                                  m.jobnimbusStage !== selectedJobnimbusStageId
                              );

                              // Add new mapping
                              newMappings.push({
                                jobnimbusStage:
                                  selectedJobnimbusStage?.name ||
                                  selectedJobnimbusStageId,
                                jobnimbusStageId:
                                  selectedJobnimbusStage?.id ||
                                  selectedJobnimbusStageId,
                                paivStage: paivStage.title,
                                paivStageId: paivStage.id,
                              });
                            }

                            // Sort mappings by JobNimbus order after update (when in edit mode)
                            const sortedMappings = isEditing
                              ? sortMappingsByJobNimbusOrder(
                                  newMappings,
                                  jobnimbusStages
                                )
                              : sortMappingsByPaivSequence(
                                  newMappings,
                                  paivStages
                                );
                            setMappings(sortedMappings);
                          }}
                          disabled={!isEditing}
                          displayEmpty
                          sx={whiteSelectSx}
                          MenuProps={whiteSelectMenuProps}
                          renderValue={(value) => {
                            if (!value) return <em>Select JobNimbus Stage</em>;
                            const stage = jobnimbusStages.find(
                              (s) => s.id === value || s.name === value
                            );
                            return stage?.name || value;
                          }}
                        >
                          <MenuItem value="">
                            <em>Select JobNimbus Stage</em>
                          </MenuItem>
                          {sortedJnStages.map((stage) => (
                            <MenuItem key={stage.id} value={stage.id}>
                              {stage.name}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    }
                  />
                );
              })}
          </Box>
        </Box>
      )}

      {/* Field Mappings Section */}
      {jobnimbusCustomFields.length > 0 && availableFlexFields.length === 0 && (
        <Box
          sx={{ mb: 3, p: 2, backgroundColor: 'info.light', borderRadius: 1 }}
        >
          <Typography variant="body2" color="info.dark">
            <strong>Note:</strong> JobNimbus custom fields are loaded, but Paiv
            fields could not be loaded. Create a new field mapping in Paiv to
            sync JobNimbus fields to Paiv fields. The Field Mappings section
            will appear once Paiv fields are available.
          </Typography>
        </Box>
      )}
      {/* Field Mappings Section */}
      {jobnimbusCustomFields.length > 0 && availableFlexFields.length > 0 && (
        <Box sx={{ mb: 3 }}>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              gap: 4,
              mb: 2,
            }}
          >
            <Box>
              <Typography variant="h6" sx={{ mb: 0.5 }}>
                Field Mappings
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Contact information (Name, Email, Address, etc.) and Photos are
                synced by default.
              </Typography>
            </Box>
            {isEditing && (
              <Button
                variant="outlined"
                startIcon={<AddIcon />}
                onClick={() => {
                  setAddFieldDialogOpen(true);
                }}
                disabled={isAddFieldButtonDisabled}
              >
                Add Field Mapping
              </Button>
            )}
          </Box>

          {enabledFieldMappings.length === 0 ? (
            <Box
              sx={{
                p: 3,
                textAlign: 'center',
                backgroundColor: 'grey.50',
                borderRadius: 1,
              }}
            >
              <Typography variant="body2" color="text.secondary">
                No field mappings configured. Click &quot;Add Field
                Mapping&quot; to map a Paiv field to a JobNimbus field.
              </Typography>
            </Box>
          ) : (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              {enabledFieldMappings.map((fieldName) => {
                const field = availableFlexFields.find(
                  (f) => f.name === fieldName
                );
                if (!field) return null;
                const mappedValue = fieldMappings[fieldName] || '';
                return (
                  <MappingRow
                    key={fieldName}
                    leftLabel="Paiv Field"
                    leftValue={field.label}
                    rightLabel="JobNimbus Field"
                    rightContent={
                      <Box
                        sx={{
                          display: 'flex',
                          gap: 1,
                          alignItems: 'flex-start',
                        }}
                      >
                        <FormControl fullWidth size="small">
                          <Select
                            value={mappedValue}
                            onChange={(e) => {
                              const newMappings = {
                                ...fieldMappings,
                                [fieldName]: e.target.value,
                              };
                              setFieldMappings(newMappings);
                            }}
                            disabled={!isEditing}
                            displayEmpty
                            sx={whiteSelectSx}
                            MenuProps={whiteSelectMenuProps}
                          >
                            <MenuItem value="">
                              <em>Select JobNimbus Field</em>
                            </MenuItem>
                            {jobnimbusCustomFields.map((jnField) => (
                              <MenuItem key={jnField.name} value={jnField.name}>
                                {jnField.label}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                        {isEditing && (
                          <Tooltip title="Remove field mapping">
                            <IconButton
                              size="small"
                              onClick={() => {
                                const newMappings = { ...fieldMappings };
                                delete newMappings[fieldName];

                                const availableFieldNames =
                                  availableFlexFields.map((f) => f.name);
                                const newEnabled = enabledFieldMappings
                                  .filter((name) => name !== fieldName)
                                  .filter((name) =>
                                    availableFieldNames.includes(name)
                                  );

                                const cleanedMappings: Record<string, string> =
                                  {};
                                newEnabled.forEach((name) => {
                                  if (newMappings[name]) {
                                    cleanedMappings[name] = newMappings[name];
                                  }
                                });

                                setFieldMappings(cleanedMappings);
                                setEnabledFieldMappings(newEnabled);
                              }}
                              sx={{
                                mt: 0.5,
                                color: 'error.main',
                                '&:hover': {
                                  backgroundColor: 'error.light',
                                  color: 'error.dark',
                                },
                              }}
                            >
                              <ClearIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                      </Box>
                    }
                  />
                );
              })}
            </Box>
          )}
        </Box>
      )}

      {/* Add Field Mapping Dialog */}
      <Dialog
        open={addFieldDialogOpen}
        onClose={() => setAddFieldDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Add Field Mapping</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Select a Paiv field to map to a JobNimbus field.
          </Typography>
          <List>
            {availableFlexFields
              .filter((field) => !enabledFieldMappings.includes(field.name))
              .map((field) => (
                <ListItem
                  key={field.name}
                  button
                  onClick={() => {
                    setEnabledFieldMappings([
                      ...enabledFieldMappings,
                      field.name,
                    ]);
                    setAddFieldDialogOpen(false);
                  }}
                  sx={{
                    '&:hover': {
                      backgroundColor: 'action.hover',
                    },
                  }}
                >
                  <ListItemText
                    primary={field.label}
                    secondary={`Field: ${field.name}`}
                  />
                </ListItem>
              ))}
          </List>
          {availableFlexFields.filter(
            (field) => !enabledFieldMappings.includes(field.name)
          ).length === 0 && (
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ textAlign: 'center', py: 2 }}
            >
              All available Paiv fields have been added.
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddFieldDialogOpen(false)}>Cancel</Button>
        </DialogActions>
      </Dialog>

      {/* Sticky Save/Cancel Footer */}
      {isEditing && (
        <Box
          sx={{
            position: 'sticky',
            bottom: 0,
            backgroundColor: 'background.paper',
            borderTop: '1px solid',
            borderColor: 'divider',
            p: 2,
            mt: 3,
            display: 'flex',
            justifyContent: 'flex-end',
            gap: 2,
            zIndex: 10,
          }}
        >
          <Button variant="outlined" onClick={handleCancel}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleSave}
            disabled={isSaving || !canSave}
          >
            {isSaving ? 'Saving...' : 'Save Integration'}
          </Button>
        </Box>
      )}

      {/* Help Dialog */}
      <Dialog
        open={helpDialogOpen}
        onClose={() => setHelpDialogOpen(false)}
        maxWidth="md"
        fullWidth
        scroll="paper"
        sx={{ '& .MuiDialog-paper': { maxHeight: '70vh' } }}
      >
        <DialogTitle>JobNimbus Integration Help</DialogTitle>
        <DialogContent dividers>
          <Typography variant="h6" sx={{ mb: 2 }}>
            Get your JobNimbus API Key
          </Typography>
          <Box component="ol" sx={{ pl: 3, mb: 3 }}>
            <li>
              <Typography variant="body1" sx={{ mb: 1 }}>
                Log into your JobNimbus account
              </Typography>
            </li>
            <li>
              <Typography variant="body1" sx={{ mb: 1 }}>
                Navigate to Settings → Integrations → API
              </Typography>
            </li>
            <li>
              <Typography variant="body1" sx={{ mb: 1 }}>
                Click &quot;New API key&quot;
              </Typography>
            </li>
            <li>
              <Typography variant="body1" sx={{ mb: 1 }}>
                Copy the generated API key and paste it into the setup form
              </Typography>
            </li>
          </Box>
          <Typography variant="h6" sx={{ mb: 2 }}>
            Add the Paiv Webhook in JobNimbus
          </Typography>
          <Typography variant="body1" sx={{ mb: 2 }}>
            After saving, you&apos;ll receive a webhook URL. Setup depends on
            your Main Object Type:
          </Typography>
          <Typography
            variant="subtitle1"
            sx={{ mt: 2, mb: 1, fontWeight: 600 }}
          >
            Contact type:
          </Typography>
          <Box component="ol" sx={{ pl: 3, mb: 3 }}>
            <li>
              <Typography variant="body1" sx={{ mb: 1 }}>
                Copy the webhook URL
              </Typography>
            </li>
            <li>
              <Typography variant="body1" sx={{ mb: 1 }}>
                In JobNimbus: Settings → Automations → Add Automation
              </Typography>
            </li>
            <li>
              <Typography variant="body1" sx={{ mb: 1 }}>
                Name: &quot;Update Paiv Contact Deal&quot; → Event based →
                Contact Created/Modified → Condition: Type is Customer → Action:
                Webhook → paste URL
              </Typography>
            </li>
          </Box>
          <Typography
            variant="subtitle1"
            sx={{ mt: 2, mb: 1, fontWeight: 600 }}
          >
            Job type (two webhooks):
          </Typography>
          <Box component="ol" sx={{ pl: 3, mb: 3 }}>
            <li>
              <Typography variant="body1" sx={{ mb: 1 }}>
                Set up the Contact webhook (same as above)
              </Typography>
            </li>
            <li>
              <Typography variant="body1" sx={{ mb: 1 }}>
                Add second Automation: &quot;Update Paiv Deal&quot; → Job
                Created/Modified → Condition: Type matches your workflow →
                Webhook → same URL
              </Typography>
            </li>
          </Box>
          <Typography variant="h6" sx={{ mb: 2 }}>
            Troubleshooting
          </Typography>
          <Box component="ul" sx={{ pl: 3 }}>
            <li>
              <Typography variant="body1" sx={{ mb: 1 }}>
                Verify your API key has the necessary permissions
              </Typography>
            </li>
            <li>
              <Typography variant="body1" sx={{ mb: 1 }}>
                Ensure the webhook URL is configured in JobNimbus
              </Typography>
            </li>
            <li>
              <Typography variant="body1" sx={{ mb: 1 }}>
                Check that stage mappings are complete
              </Typography>
            </li>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setHelpDialogOpen(false)} variant="contained">
            Got it
          </Button>
        </DialogActions>
      </Dialog>

      <ConfirmationModal
        open={isDisableOpen}
        onCancel={() => setIsDisableOpen(false)}
        onConfirm={handleDisable}
        title="Disable JobNimbus Integration"
        message="Are you sure you want to disable this JobNimbus integration? This will stop syncing data between JobNimbus and Paiv. Remember to remove the webhook from your JobNimbus settings."
        confirmText="Disable"
        severity="warning"
      />

      <ConfirmationModal
        open={isRemoveOpen}
        onCancel={() => setIsRemoveOpen(false)}
        onConfirm={handleRemove}
        title="Remove JobNimbus Integration"
        message="Are you sure you want to permanently remove this JobNimbus integration? This action cannot be undone. Remember to remove the webhook from your JobNimbus settings."
        confirmText="Remove"
        severity="error"
      />

      <Dialog
        open={webhookDialogOpen}
        onClose={() => setWebhookDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>JobNimbus Integration Saved Successfully!</DialogTitle>
        <DialogContent>
          <Typography variant="body1" sx={{ mb: 2 }}>
            Your JobNimbus integration has been saved. Please copy the webhook
            URL below and add it to your JobNimbus settings:
          </Typography>
          <WebhookUrlDisplay
            url={webhookUrl || ''}
            onHelpClick={() => setHelpDialogOpen(true)}
          />
          <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
            Add this webhook URL to your JobNimbus account settings to enable
            real-time data synchronization.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setWebhookDialogOpen(false)}
            variant="contained"
          >
            Got it
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};
